import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, MapPin, Pin, Link as LinkIcon } from 'lucide-react';
import ChatCard from '../ChatCard';
import { useTruncatedText } from '../useTruncatedText';
import '../ChatCard.css';
import '../organization-events/EventCards.css';
import './ProactiveEventCard.css';

const COLLAPSED_LINES = 3;

/**
 * Render description text with minimal **bold** support.
 * Splits on `**...**` and wraps matches in <strong>.
 */
const renderDescription = (text) => {
  if (!text) return null;
  const segments = text.split(/(\*\*[^*]+\*\*)/g);
  return segments.map((segment, idx) => {
    if (segment.startsWith('**') && segment.endsWith('**')) {
      return <strong key={idx}>{segment.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={idx}>{segment}</React.Fragment>;
  });
};

/**
 * Short, readable label for an event link — "docs.google.com" reads better
 * in a card than a 100-character Google Sheets URL.
 *
 * Returns null when the value isn't a usable http(s) URL, which also acts as
 * the scheme guard: anything else (javascript:, mailto:, free text the
 * OfficeRND admin typed into the links field) renders no anchor at all.
 */
const getLinkLabel = (link) => {
  if (!link) return null;
  try {
    const url = new URL(link);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

/**
 * Single-event proactive card.
 *
 * Renders ONE upcoming event suggested by the backend's
 * `organization_event_proactive` flow. Layout reuses the `event-card-*`
 * classes from `EventCards.css`. Icons come from `lucide-react` (the
 * backend no longer ships emojis in `raw_output`).
 *
 * @param {object|null} data        Normalized data from `getProactiveEventData`
 * @param {function}    [onImageLoad]  Fires when card image loads (scroll-anchor)
 * @param {*}           [collapseSignal]  When this changes, the expanded
 *                                        description collapses (used when
 *                                        the user sends a new message).
 */
const ProactiveEventCard = ({ data, onImageLoad, collapseSignal }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(false);
  }, [collapseSignal]);

  const toggle = useCallback(() => setIsExpanded(v => !v), []);

  const { ref: descRef, truncatedText, isLong } = useTruncatedText(
    data?.description || '',
    COLLAPSED_LINES
  );

  if (!data) return null;

  const { title, dateLine, timeLine, locationName, where, description, imageUrl, link } = data;

  const datetimeText = timeLine ? `${dateLine} · ${timeLine}` : dateLine;
  const linkLabel = getLinkLabel(link);
  const visibleText = isExpanded ? description : truncatedText;

  return (
    <div className="proactive-event-card">
      <ChatCard image={imageUrl} imageAlt={title} onImageLoad={onImageLoad}>
        <h4 className="event-card-title">{title}</h4>

        {datetimeText && (
          <p className="event-card-datetime">
            <span className="event-card-icon" aria-hidden="true">
              <Calendar size={16} />
            </span>
            <span className="event-card-row-text">{datetimeText}</span>
          </p>
        )}

        {locationName && (
          <p className="event-card-location">
            <span className="event-card-icon" aria-hidden="true">
              <MapPin size={16} />
            </span>
            <span className="event-card-row-text">{locationName}</span>
          </p>
        )}

        {where && (
          <p className="event-card-venue">
            <span className="event-card-icon" aria-hidden="true">
              <Pin size={14} />
            </span>
            <span className="event-card-row-text">{where}</span>
          </p>
        )}

        {description && (
          <p
            ref={descRef}
            className={`event-card-description${isExpanded ? ' is-expanded' : ''}`}
          >
            {renderDescription(visibleText)}
            {isLong && (
              <>
                {' '}
                <button
                  type="button"
                  className="event-card-toggle"
                  onClick={toggle}
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? 'Hide event details' : 'Show full event details'}
                >
                  {isExpanded ? 'Hide details' : 'Show more'}
                </button>
              </>
            )}
          </p>
        )}

        {/* Opens in a new tab: the app runs inside an iframe
            (member.kiln.com/concierge), and shared Google Docs/Sheets refuse
            to be framed at all — so an in-app modal would render blank. */}
        {linkLabel && (
          <p className="event-card-link">
            <span className="event-card-icon" aria-hidden="true">
              <LinkIcon size={14} />
            </span>
            <a
              className="event-card-link-anchor"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {linkLabel}
            </a>
          </p>
        )}
      </ChatCard>
    </div>
  );
};

export default React.memo(ProactiveEventCard);
